import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Divider,
  Button,
  Flex,
  ModalCloseButton,
  Box,
  Text,
} from "@chakra-ui/react";
import {
  GOOGLE_CLIENT_ID,
  DOMAIN_NAME,
  GOOGLE_AUTH_URL,
} from "../../../_actions/endpoints";
import { useOauth2Login } from "../../../_utils/hooks";
import { analyticsTrackEvent, genericAppConnect } from "../../../_utils/common";
import { AppIdType } from "../../../_utils/constants";
import { handleHTTPError } from "../../../_actions/errorHandlerActions";
import axios from "axios";
import { showToast } from "../../../_utils/showToast";
import { useTranslation } from "react-i18next";

const GoogleDriveConnectApp = forwardRef((props, ref) => {
  const { t } = useTranslation(["planner"]);
  const signal = axios.CancelToken.source();
  const [open, setOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    setOpen,
  }));

  const [appConnecting, setAppConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const SCOPES = [
    "profile",
    "email",
    "https://www.googleapis.com/auth/drive.metadata.readonly",
    "https://www.googleapis.com/auth/drive.readonly",
  ];

  const handleModalClose = () => {
    setOpen(false);
    setAppConnecting(false);
  };

  const onSuccessConnect = (authRes) => {
    let errorInScopes = false;

    const permittedScopes = authRes.scope ? authRes.scope.split(" ") : [];
    SCOPES.forEach((scope) => {
      if (!permittedScopes.includes(scope)) {
        errorInScopes = true;
      }
    });

    if (errorInScopes) {
      setErrorMessage(t("connect_sheets.permission_insufficient"));
    } else if (authRes) {
      setAppConnecting(true);
      genericAppConnect(
        AppIdType.GOOGLE_DRIVE,
        {
          code: authRes.code,
        },
        signal.token
      )
        .then((res) => {
          handleModalClose();
          showToast({
            description: t("connect_sheets.drive_Connected"),
            title: t("connect_sheets.drive"),
            status: "success",
          });

          props.onSuccess(res);

          analyticsTrackEvent("connectedAccount", {
            accountType: AppIdType.GOOGLE_DRIVE,
          });
        })
        .catch((error) => {
          setAppConnecting(false);
          if (error.response.data.non_field_errors) {
            error.response.data.non_field_errors.map((error) => {
              showToast({
                description: error,
                title: "Error",
                status: "error",
                duration: null,
              });
            });
          } else {
            props.handleHTTPError(error, props);
          }
        });
    }
  };

  const { requestSignIn, loading: consentLoading } = useOauth2Login({
    id: "google-drive-app",
    authorizationUrl: GOOGLE_AUTH_URL,
    clientId: GOOGLE_CLIENT_ID,
    redirectUri: DOMAIN_NAME,
    responseType: "code",
    onSuccess: onSuccessConnect,
    onFailure: (res) => {},
    scope: SCOPES.join(" "),
    extraQueryParams: {
      include_granted_scopes: false,
      access_type: "offline",
      prompt: "consent select_account",
    },
  });

  useEffect(() => {
    if (open) {
      requestSignIn();
    } else {
      setOpen(false);
      setAppConnecting(false);
      setErrorMessage(null);
    }
    return () => {};
  }, [open]);

  return (
    <Modal isOpen={open} onClose={handleModalClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("connect_sheets.drive_heading")}</ModalHeader>
        <ModalCloseButton />
        <Divider my="1" />
        <ModalBody>
          {errorMessage && (
            <Text color="brand.danger" textAlign="center" my={4}>
              {errorMessage}
            </Text>
          )}
          <Flex w="100%" justify="center" align="center">
            <Button
              variant="solid"
              onClick={() => {
                requestSignIn();
              }}
              disabled={consentLoading || appConnecting}
              isLoading={consentLoading || appConnecting}
              loadingText={t("connect_sheets.authorize")}
            >
              {t("connect_sheets.authorize")}
            </Button>
          </Flex>
        </ModalBody>
        <Divider my="1" />
        <ModalFooter>
          <Button
            onClick={(e) => {
              handleModalClose();
            }}
            variant="outline"
          >
            {t("cancel", { ns: "common" })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

GoogleDriveConnectApp.propTypes = {
  onSuccess: PropTypes.func,
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, { handleHTTPError }, null, {
  forwardRef: true,
})(GoogleDriveConnectApp);
